import { 
    Box, 
    Chip, 
    CircularProgress, 
    IconButton, 
    Typography,
    Menu,
    MenuItem,
    Divider,
    Tooltip
} from "@mui/material";
import { createNewCollectionResource, Resource, ResourceStatus, ResourceType } from "../../../../models/resource";
import { 
    Archive, 
    Delete, 
    EditDocument, 
    MoreVert, 
    Publish,
    Description,
    VideoLibrary,
    StickyNote2,
    ContentCopy,
    Edit
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import DeleteResourceDialog from "./delete-resource-dialog";
import PublishResourceDialog from "./publish-resource-dialog";
import { useNavigate } from "react-router-dom";
import { useClassroomActions } from "../../../../hooks/useClassroomActions";
import sanitizeString from "../../../../utils/sanitize-name";
import { useSnackbar } from "../../../../components/snackbar";
import { useDomainActions } from "../../../../hooks/useDomainActions";
import { Hub } from "../../../../models/hub";
import isHubActivated from "../../../../utils/is-hub-activated";

interface ResourceCardProp {
    classroom_id: number;
    collection_id: number;
    role: "admin" | "colab";
    resource: Resource;
    setResource: React.Dispatch<React.SetStateAction<Resource>>;
    openDialog: (key: string) => void;
    updatingResource: number;
    uploadPercent: number | null;
    setUpdatingResource: React.Dispatch<React.SetStateAction<number>>;
    hub: Hub;
    setDeleting: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ResourceCard({ 
    classroom_id,
    collection_id,
    role,
    resource, 
    setResource,
    openDialog,
    updatingResource,
    uploadPercent,
    setUpdatingResource,
    hub,
    setDeleting
}: ResourceCardProp) {
    const navigate = useNavigate();

    const { 
        updateResource, 
        deleteResource
    } = useClassroomActions();

    const { showMessage } = useSnackbar();
    
    const [firstPublishResource, setFirstPublishResource] = useState(false);

    const [deleteResourceDialog, setDeleteResourceDialog] = useState(false);

    const [copy, setCopy] = useState(false);
    
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const menuOpen = Boolean(menuAnchorEl);

    const handleCardClick = () => {
        // if (updatingResource === resource.id || menuAnchorEl || deleteResourceDialog || firstPublishResource) {
        //     return;
        // }
        setResource(resource);
        openDialog("edit");
        handleMenuClose();
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const _deleteResource = async (resource: Resource) => {
        setDeleting(true);
        await deleteResource(Number(classroom_id), Number(collection_id), resource.id);
        setDeleting(false);
    };

    const publishResource = async () => {
        handleMenuClose();
        if (!isHubActivated(hub)) {
            showMessage('Hub is not activated', 1);
            return;
        }

        if (resource.status === ResourceStatus.DRAFT) {
            setFirstPublishResource(true);
        } else {
            const newRes = {
                ...resource,
                status: ResourceStatus.PUBLISHED
            }
            await updateResource(Number(classroom_id), Number(collection_id), newRes);
        }
    };

    const archiveResource = async () => {
        const newRes = {
            ...resource,
            status: ResourceStatus.ARCHIVED
        }
        handleMenuClose();
        await updateResource(Number(classroom_id), Number(collection_id), newRes);
    };

    const _updateResource = async (res: Resource) => {
        setUpdatingResource(res.id);
        
        // if ((resource.type === ResourceType.MEDIA || resource.type === ResourceType.FILE) && resource.file) {
        //     resource.url = `${resource.type}/${resource.file?.name}`|| `${sanitizeString(resource.title)}`;
        // }
        
        await updateResource(res.classroom_id, res.collection_id, res);
        
        setResource(createNewCollectionResource());
        setUpdatingResource(0);
    };

    const editNote = (url: string) => {
        navigate(url)
    };

    const handleEditNote = () => {
        
        if (role === "admin") {
            editNote(`/classroom/${classroom_id}/library/collections/${collection_id}/notes/${resource.title}?id=${resource.url}`);
        } else {
            // come back to this
        }
    };

    const handleCopy = () => {
        if (copy) {
            setCopy(false);
        } else {
                const resourceString = JSON.stringify(resource);
                navigator.clipboard.writeText(resourceString).then(() => {
                    console.log('Resource copied to clipboard!');
                }).catch(err => {
                    console.error('Failed to copy!', err);
                });
                setCopy(true);
        }
        handleMenuClose();
    }

    const handleDelete = () => {
        handleMenuClose();
        setDeleteResourceDialog(true);
    };

    const getResourceConfig = () => {
        switch (resource.type) {
            case ResourceType.NOTE:
                return {
                    icon: <StickyNote2 sx={{ fontSize: 20, color: '#4f46e5' }} />,
                    accentColor: '#4f46e5',
                    bgColor: '#f8fafc'
                };
            case ResourceType.MEDIA:
                return {
                    icon: <VideoLibrary sx={{ fontSize: 20, color: '#e11d48' }} />,
                    accentColor: '#e11d48',
                    bgColor: '#f8fafc'
                };
            default:
                return {
                    icon: <Description sx={{ fontSize: 20, color: '#0891b2' }} />,
                    accentColor: '#0891b2',
                    bgColor: '#f8fafc'
                };
        }
    };

    const getStatusConfig = () => {
        switch (resource.status) {
            case ResourceStatus.PUBLISHED:
                return {
                    label: 'Published',
                    color: 'grey.700',
                    bgColor: '#67d680',
                };
            case ResourceStatus.DRAFT:
                return {
                    label: 'Draft',
                    color: '#495057',
                    bgColor: '#e2e3e5',
                };
            case ResourceStatus.ARCHIVED:
                return {
                    label: 'Archived',
                    color: '#721c24',
                    bgColor: '#fcc2c8',
                };
        }
    };

  
    return (
        <Box
            sx={{
                p: 2,
                borderRadius: 5,
                backgroundColor: '#fff',
                border: '1px solid #f1f5f9',
                transition: 'all 0.2s ease-out',
                borderColor: getStatusConfig().bgColor,
                height: "100px",
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                opacity: copy ? 0.5 : 1,
                borderStyle: copy ? 'dashed' : 'solid',
                filter: copy ? 'grayscale(100%) blur(1px)' : 'none',
            }}
        >


            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',  mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                    <Box sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        backgroundColor: getResourceConfig().bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `1px solid ${getResourceConfig().accentColor}15`,
                    }}>
                        {resource.type === (ResourceType.MEDIA) ? (
                            (updatingResource === resource.id || resource.url && resource.url.endsWith(".mp4")) ? (
                                getResourceConfig().icon
                            ) : (
                                <img
                                    src={`https://d1at3arxf72ma9.cloudfront.net/classrooms/${resource.classroom_id}/resources/${resource.url}`}
                                    alt="resource"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            )
                        ) : 
                            getResourceConfig().icon
                        }
                    </Box>
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                fontSize: '1rem',
                                lineHeight: 1.4,
                                color: '#111827',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                            }}
                        >
                            {resource.title}
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    className="resource-menu-btn"
                    onClick={handleMenuOpen}
                    size="small"
                    sx={{
                        ml: 1,
                        color: 'grey.900',
                        opacity: 0.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            opacity: 1,
                            backgroundColor: '#f1f5f9',
                        }
                    }}
                >
                    <MoreVert fontSize="small" />
                </IconButton>
            </Box>


            {/* Footer */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                pt: 2,
                borderTop: '1px solid rgb(245, 242, 242)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: '#64748b',
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                        }}
                    >
                        {new Date(resource.last_updated).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                        })}
                    </Typography>
                    
                    <Box
                        sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            backgroundColor: '#cbd5e1',
                        }}
                    />
                    
                    <Chip
                        label={getStatusConfig().label}
                        size="small"
                        sx={{
                            backgroundColor: getStatusConfig().bgColor,
                            color: getStatusConfig().color,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: 22,
                            border: 'none',
                            '& .MuiChip-label': {
                                px: 1.5,
                            }
                        }}
                    />
                </Box>
                
                {!uploadPercent && resource.type === ResourceType.NOTE && (
                    <Tooltip title="Edit Note" arrow>
                        <IconButton onClick={handleEditNote}>
                            <EditDocument fontSize="small" sx={{ color: '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                )}

                {updatingResource === resource.id && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress 
                            size={18} 
                            sx={{ color: getResourceConfig().accentColor }}
                        />
                        {uploadPercent !== null && (
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                            {uploadPercent}%
                        </Typography>
                        )}
                    </Box>
                )}

            </Box>

            <Menu 
                anchorEl={menuAnchorEl} 
                open={menuOpen} 
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    sx: {
                    mt: 0.5,
                    minWidth: 150,
                    borderRadius: 2,
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid #e5e7eb',
                    py: 1,
                    }
                }}
            >
                <MenuItem 
                    key="edit"
                    onClick={handleCardClick}
                    sx={{ 
                        gap: 2, 
                        px: 2,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        '&:hover': {
                            backgroundColor: '#f8fafc'
                        }
                    }}
                >
                    <Edit fontSize="small" sx={{ color: '#64748b' }} />
                    Edit
                </MenuItem>,

                <Divider key="divider-note" sx={{ }} />

                {resource.status === ResourceStatus.PUBLISHED ? (
                    <MenuItem 
                        onClick={archiveResource}
                        sx={{ 
                            gap: 2, 
                            py: 1.25,
                            px: 2,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            '&:hover': {
                            backgroundColor: '#f8fafc'
                            }
                        }}
                    >
                        <Archive fontSize="small" sx={{ color: '#64748b' }} />
                        Archive
                    </MenuItem>
                ) : (
                    <MenuItem 
                        onClick={publishResource}
                        sx={{ 
                            gap: 2, 
                            py: 1.25,
                            px: 2,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: '#f8fafc'
                            }
                        }}
                    >
                        <Publish fontSize="small" sx={{ color: '#64748b' }} />
                        Publish
                    </MenuItem>
                )}

                <Divider sx={{ my: 1 }} />
                
                <MenuItem 
                    onClick={handleCopy}
                    sx={{ 
                        gap: 2, 
                        py: 1.25,
                        px: 2,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        '&:hover': {
                            backgroundColor: '#f8fafc'
                        }
                    }}
                >
                    <ContentCopy fontSize="small" sx={{ color: '#64748b' }} />
                    {copy ? "Cancel" : "Copy"}
                </MenuItem>

                <Divider sx={{ my: 1 }} />

                <MenuItem 
                    onClick={handleDelete}
                    sx={{ 
                    gap: 2, 
                    py: 1.25,
                    px: 2,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#dc2626',
                    '&:hover': {
                        backgroundColor: '#fef2f2'
                    }
                    }}
                >
                    <Delete fontSize="small" />
                    Delete
                </MenuItem>
            </Menu>

            <PublishResourceDialog
                resource={resource}
                open={firstPublishResource}
                setOpen={setFirstPublishResource}
                updateResource={_updateResource}
            /> 

          
            <DeleteResourceDialog
                resource={resource}
                open={deleteResourceDialog}
                setOpen={setDeleteResourceDialog}
                deleteResource={_deleteResource}
            />
            

        </Box>    
    );
}